import {
  ApplicationRef,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  EnvironmentInjector,
  Renderer2,
  computed,
  createComponent,
  createEnvironmentInjector,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { outputToObservable, toSignal } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';

import { MessageStateComponent } from '../message-state/message-state.component';
import { MessageStateConfig } from '../message-state/message-state.interface';

export type SkeletonVariant =
  | 'bar'
  | 'card-data'
  | 'card'
  | 'card-grid'
  | 'rental-card'
  | 'gallery'
  | 'gallery-thumb'
  | 'metric'
  | 'table-row'
  | 'table-body-row'
  | 'toolbar'
  | 'calendar-cell';

/**
 * SkeletonDirective — unified loading / empty / error state manager.
 *
 * Attach to any container that wraps repeated content (@for loops, tables, grids).
 * The directive owns three mutually exclusive injected states:
 *
 *  • loading + repeat > 0 → hides real children, injects N shimmer placeholder
 *    divs. Shape is driven by `nbxSkeletonVariant` (CSS class), so sizing comes
 *    from padding + line-height rules in skeleton.css — no explicit height needed.
 *
 *  • loading + repeat = 0 → CSS overlay mode: sets [data-skeleton] on the host
 *    so global CSS transforms the real DOM in-place (no JS needed).
 *
 *  • error (and not loading) → hides children, mounts nbx-message-state with
 *    error severity. Emits (nbxSkeletonAction) when user clicks the retry button.
 *
 *  • empty (and not loading, not error) → hides children, mounts nbx-message-state
 *    with info severity.
 *
 *  • otherwise → shows children normally.
 *
 * @example — table
 * ```html
 * <div class="flex flex-col gap-2"
 *      [nbxSkeleton]="loading()"
 *      [nbxSkeletonRepeat]="6"
 *      nbxSkeletonVariant="table-row"
 *      [nbxSkeletonError]="error()"
 *      [nbxSkeletonEmpty]="!loading() && !error() && items().length === 0"
 *      (nbxSkeletonAction)="reload()">
 *   @for (item of items(); track item.id) { <tr>...</tr> }
 * </div>
 * ```
 *
 * @example — card grid
 * ```html
 * <div class="grid grid-cols-3 gap-4"
 *      [nbxSkeleton]="loading()"
 *      [nbxSkeletonRepeat]="3"
 *      nbxSkeletonVariant="card-data"
 *      [nbxSkeletonEmpty]="!loading() && items().length === 0">
 *   @for (item of items(); track item.id) { <nbx-card-data ... /> }
 * </div>
 * ```
 */
@Directive({
  selector: '[nbxSkeleton]',
  standalone: true,
  host: {
    '[attr.data-skeleton]': '_overlayAttr()',
    '[attr.aria-busy]': 'nbxSkeleton()',
  },
})
export class SkeletonDirective {
  private readonly _el = inject(ElementRef<HTMLElement>);
  private readonly _renderer = inject(Renderer2);
  private readonly _envInjector = inject(EnvironmentInjector);
  private readonly _appRef = inject(ApplicationRef);
  private readonly _destroyRef = inject(DestroyRef);

  private _placeholders: HTMLElement[] = [];
  private _messageRef: ComponentRef<MessageStateComponent> | null = null;
  private _messageInjector: EnvironmentInjector | null = null;

  protected readonly _overlayAttr = computed(() =>
    this.nbxSkeleton() && this.nbxSkeletonRepeat() === 0 ? '' : null
  );

  nbxSkeleton = input.required<boolean>();

  nbxSkeletonRepeat = input<number>(0);

  nbxSkeletonVariant = input<SkeletonVariant>('bar');

  nbxSkeletonHeight = input<string | null>(null);

  nbxSkeletonError = input<boolean>(false);

  nbxSkeletonErrorConf = input<MessageStateConfig>({});

  nbxSkeletonEmpty = input<boolean>(false);

  nbxSkeletonEmptyConf = input<MessageStateConfig>({});

  readonly nbxSkeletonAction = output<void>();

  constructor() {
    effect(() => {
      const loading = this.nbxSkeleton();
      const repeat = this.nbxSkeletonRepeat();
      const error = this.nbxSkeletonError();
      const empty = this.nbxSkeletonEmpty();
      const errorConf = this.nbxSkeletonErrorConf();
      const emptyConf = this.nbxSkeletonEmptyConf();

      this._cleanup();

      if (loading && repeat > 0) {
        this._hideRealChildren();
        this._injectPlaceholders(repeat);
      } else if (!loading && error) {
        this._hideRealChildren();
        this._injectMessage({ severity: 'error', ...errorConf });
      } else if (!loading && !error && empty) {
        this._hideRealChildren();
        this._injectMessage({ severity: 'info', ...emptyConf });
      } else {
        this._showRealChildren();
      }
    });

    this._destroyRef.onDestroy(() => this._cleanup());
  }

  private _injectPlaceholders(count: number): void {
    const el = this._el.nativeElement as HTMLElement;
    const variant = this.nbxSkeletonVariant();
    const customHeight = this.nbxSkeletonHeight();

    for (let i = 0; i < count; i++) {
      const div = this._renderer.createElement('div') as HTMLElement;
      this._renderer.addClass(div, 'nbx-skeleton-placeholder');

      if (variant !== 'bar') {
        this._renderer.addClass(div, `nbx-skeleton--${variant}`);
      } else if (customHeight) {
        this._renderer.setStyle(div, 'height', customHeight);
      }

      this._renderer.appendChild(el, div);
      this._placeholders.push(div);
    }
  }

  private _injectMessage(config: MessageStateConfig): void {
    const messageInjector = createEnvironmentInjector([], this._envInjector);
    this._messageInjector = messageInjector;
    const msgRef = createComponent(MessageStateComponent, {
      environmentInjector: messageInjector,
    });

    msgRef.setInput('config', config);

    const action = toSignal(
      outputToObservable(msgRef.instance.action).pipe(map(() => Symbol('messageAction'))),
      { initialValue: null, injector: messageInjector }
    );

    effect(
      () => {
        if (action() === null) {
          return;
        }
        this.nbxSkeletonAction.emit();
      },
      { injector: messageInjector }
    );

    this._appRef.attachView(msgRef.hostView);
    this._renderer.appendChild(this._el.nativeElement, msgRef.location.nativeElement);

    this._messageRef = msgRef;
  }

  private _hideRealChildren(): void {
    for (const child of this._realChildren()) {
      this._renderer.setStyle(child, 'display', 'none');
    }
  }

  private _showRealChildren(): void {
    for (const child of this._realChildren()) {
      this._renderer.removeStyle(child, 'display');
    }
  }

  private _realChildren(): HTMLElement[] {
    const injectedNode = this._messageRef?.location.nativeElement as HTMLElement | undefined;
    return Array.from(
      (this._el.nativeElement as HTMLElement).children as HTMLCollectionOf<HTMLElement>
    ).filter(
      child => !child.classList.contains('nbx-skeleton-placeholder') && child !== injectedNode
    );
  }

  private _cleanup(): void {
    for (const p of this._placeholders) {
      if (p.parentNode) {
        this._renderer.removeChild(this._el.nativeElement, p);
      }
    }
    this._placeholders = [];

    if (this._messageRef) {
      this._appRef.detachView(this._messageRef.hostView);
      const domEl = this._messageRef.location.nativeElement as HTMLElement;
      if (domEl.parentNode) {
        this._renderer.removeChild(this._el.nativeElement, domEl);
      }
      this._messageRef.destroy();
      this._messageRef = null;
    }

    if (this._messageInjector) {
      this._messageInjector.destroy();
      this._messageInjector = null;
    }
  }
}
